import { create } from 'zustand'
import { persist } from 'zustand/middleware'

/**
 * Nav Panel Store — drie-lagen navigatie (Sprint A).
 *
 * Bewaart PER SCHERMTYPE (sleutel = route-prefix, bv. "offerte", "materialen", "crm")
 * de paneel-/mode-staat, zodat een gebruiker context houdt bij het wisselen van scherm.
 *
 *   mode: 1 = enkel scherm | 2 = contextueel zijpaneel | 3 = splitscreen
 *   panelOpen: of het paneel open is
 *   activeTab: 'materialen' | 'klanten' | 'projecten'
 *   splitRatio: breedte linker pane als fractie van de workspace (mode 3)
 *
 * Patroon consistent met authStore/themeStore (Zustand + persist).
 */
export const DEFAULT_SCREEN_STATE = {
  mode: 1,
  panelOpen: false,
  activeTab: 'materialen',
  splitRatio: 0.65,
}

export const useNavPanelStore = create(
  persist(
    (set, get) => ({
      // { [schermtype]: { mode, panelOpen, activeTab, splitRatio } }
      screens: {},
      // Transient (NIET gepersisteerd): materiaal waarnaar het paneel moet scrollen/oplichten.
      highlightMaterialId: null,
      // Transient: handler die de actieve materiaal-tab registreert om "Gebruik in offerte"
      // af te handelen (paneel toont de knop alleen als er een handler is). Niet gepersisteerd.
      useMateriaalHandler: null,
      // Transient: materiaal dat momenteel uit het paneel gesleept wordt (drag-and-drop naar de
      // offerte-werkruimte). null = er wordt niet gesleept. Niet gepersisteerd.
      draggingMateriaal: null,

      getStateForScreen: (schermtype) => ({
        ...DEFAULT_SCREEN_STATE,
        ...(get().screens[schermtype] || {}),
      }),

      _patch: (schermtype, partial) =>
        set((state) => ({
          screens: {
            ...state.screens,
            [schermtype]: {
              ...DEFAULT_SCREEN_STATE,
              ...(state.screens[schermtype] || {}),
              ...partial,
            },
          },
        })),

      setMode: (schermtype, mode) => {
        // Mode 1 sluit logisch ook het paneel.
        get()._patch(schermtype, mode === 1 ? { mode, panelOpen: false } : { mode, panelOpen: true })
      },
      setPanelOpen: (schermtype, open) => get()._patch(schermtype, { panelOpen: !!open }),
      setActiveTab: (schermtype, tab) => get()._patch(schermtype, { activeTab: tab }),
      setSplitRatio: (schermtype, ratio) => {
        const clamped = Math.max(0.25, Math.min(0.85, Number(ratio) || DEFAULT_SCREEN_STATE.splitRatio))
        get()._patch(schermtype, { splitRatio: clamped })
      },

      setHighlightMaterial: (id) => set({ highlightMaterialId: id }),

      // "Gebruik in offerte": de actieve materiaal-tab registreert/clear't een handler.
      registerUseMateriaal: (fn) => set({ useMateriaalHandler: fn }),
      clearUseMateriaal: (fn) => set((s) => (s.useMateriaalHandler === fn ? { useMateriaalHandler: null } : {})),

      // Drag-and-drop: paneelrij meldt start/einde van het slepen.
      setDraggingMateriaal: (mat) => set({ draggingMateriaal: mat || null }),

      // Open het paneel op de materialen-tab en scroll naar een materiaal (laag 1→2 brug).
      openMateriaal: (schermtype, materiaalId) => {
        const cur = get().screens[schermtype] || {}
        const mode = cur.mode && cur.mode >= 2 ? cur.mode : 2
        get()._patch(schermtype, { mode, panelOpen: true, activeTab: 'materialen' })
        set({ highlightMaterialId: materiaalId })
      },
    }),
    {
      name: 'orion-nav-panel',
      // Alleen de schermstate persisteren, geen acties/afgeleide state.
      partialize: (state) => ({ screens: state.screens }),
    }
  )
)

export default useNavPanelStore
